import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div style={CARD}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "#8a8e88" }}>{label}</div>
      <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const num = (n: number) => n.toLocaleString();

export function MlsOps() {
  const clients = useQuery({ queryKey: ["clients"], queryFn: api.clients });
  const [clientId, setClientId] = useState("");
  const [openOffice, setOpenOffice] = useState<string | null>(null);
  // Each snapshot is three heavy Displet-replica aggregates; keep it fresh for a
  // minute so switching windows/tabs doesn't re-run them on every focus.
  const ops = useQuery({ queryKey: ["mls-ops", clientId], queryFn: () => api.mlsOps(clientId), enabled: !!clientId, staleTime: 60_000 });
  const roster = useQuery({
    queryKey: ["office-roster", clientId, openOffice],
    queryFn: () => api.officeRoster(clientId, openOffice!),
    enabled: !!clientId && !!openOffice,
    staleTime: 60_000,
  });

  if (clients.isLoading) return <Loading />;
  if (clients.error) return <ErrorNote error={clients.error} />;
  const clientList = clients.data ?? [];

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>MLS Ops</h1>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Live roster, offices and licenses for a client, read from the Displet platform.</div>
        </div>
        <select value={clientId} onChange={(e) => { setClientId(e.target.value); setOpenOffice(null); }} style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #e2e0d8", borderRadius: 7, minWidth: 220 }}>
          <option value="">Choose a client…</option>
          {clientList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {!clientId ? (
        <div style={{ ...CARD, color: "#8a8e88", fontSize: 13 }}>Pick a client to see its live MLS roster and feeds snapshot.</div>
      ) : ops.isLoading ? (
        <Loading />
      ) : ops.error ? (
        <ErrorNote error={ops.error} />
      ) : !ops.data ? (
        // Offline/paused query: pending but not "loading" and no error — render a
        // loader rather than dereferencing undefined data.
        <Loading />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <Tile label="Users" value={num(ops.data!.users.total)} />
            <Tile label="Realtors" value={num(ops.data!.users.realtors)} />
            <Tile label="MLS members" value={num(ops.data!.users.mlsMembers)} />
            <Tile label="Email verified" value={num(ops.data!.users.emailVerified)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
            <div style={CARD}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Top offices</div>
              <div style={{ fontSize: 11.5, color: "#8a8e88", marginBottom: 6 }}>Choose an office to see its agents.</div>
              {ops.data!.offices.length === 0 ? (
                <div style={{ color: "#8a8e88", fontSize: 13 }}>No offices on file.</div>
              ) : (
                ops.data!.offices.map((o) => {
                  const open = openOffice === o.key;
                  return (
                    <div key={o.key} style={{ borderTop: "1px solid #f4f3ee" }}>
                      <button
                        onClick={() => setOpenOffice(open ? null : o.key)}
                        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", background: "none", border: 0, cursor: "pointer", fontSize: 12.5, textAlign: "left", color: "inherit" }}
                      >
                        <span style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name={open ? "chevron-down" : "chevron-right"} size={13} color="#8a8e88" />
                          {o.name}
                        </span>
                        <span style={{ color: "#8a8e88", fontFamily: "'Space Mono',monospace" }}>{num(o.agents)}</span>
                      </button>
                      {open && (
                        <div style={{ paddingBottom: 8 }}>
                          {roster.isLoading ? (
                            <div style={{ fontSize: 12, color: "#8a8e88", padding: "2px 0 6px 19px" }}>Loading agents…</div>
                          ) : roster.error ? (
                            <div style={{ fontSize: 12, color: "#b3261e", padding: "2px 0 6px 19px" }}>Couldn't load this office.</div>
                          ) : (roster.data?.agents.length ?? 0) === 0 ? (
                            <div style={{ fontSize: 12, color: "#8a8e88", padding: "2px 0 6px 19px" }}>No agents listed.</div>
                          ) : (
                            <>
                              {roster.data!.agents.map((a) => (
                                <div key={a.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0 3px 19px", fontSize: 12 }}>
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {a.name}{a.role ? <span style={{ color: "#8a8e88" }}> · {a.role}</span> : null}
                                  </span>
                                  <span style={{ color: "#8a8e88", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.email ?? "—"}</span>
                                </div>
                              ))}
                              {roster.data!.truncated && (
                                <div style={{ fontSize: 11, color: "#8a8e88", padding: "3px 0 4px 19px" }}>Showing the first 200.</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div style={CARD}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Licenses by state</div>
              {ops.data!.licenseStates.length === 0 ? (
                <div style={{ color: "#8a8e88", fontSize: 13 }}>No licenses on file.</div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ops.data!.licenseStates.map((s) => (
                    <span key={s.state} style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: "#eef4f1", color: "#00543a" }}>
                      {s.state} <span style={{ fontFamily: "'Space Mono',monospace", color: "#4a7a63" }}>{num(s.count)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
